// src/hooks/tokens/compare/useProcessedData.ts

import { useMemo } from 'react';
import { MetricKeys } from '@/constants/metrics';
import type { RangeKey, SeriesData } from '@/utils/tokens/dataProcessing';
import { alignSeriesData, computeReturnsMap, computeDailyReturns } from '@/utils/tokens/dataProcessing';
import { computeAllRollingMetrics } from '@/utils/tokens/rollingMetrics';

interface ProcessedDataResult {
  alignedSeries: Record<string, SeriesData>;
  dailyReturnsMap: Record<string, number[]>;
  btcReturns: number[];
  computedMetrics: Record<string, Record<string, SeriesData>>;
  hasData: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to process historical data and compute derived metrics
 */
export function useProcessedData(
  historyData: Array<{ ticker: string; metrics?: Array<{ dates?: Array<string | number>; values?: (number | null)[] }> }> | undefined,
  tickers: string[],
  range: RangeKey,
  metricKey: string,
  isLoading: boolean,
  error: Error | null
): ProcessedDataResult {
  return useMemo(() => {
    if (!historyData || historyData.length === 0) {
      return {
        alignedSeries: {},
        dailyReturnsMap: {},
        btcReturns: [],
        computedMetrics: {},
        hasData: false,
        isLoading,
        error: error ? error.message : null,
      };
    }

    // Align series data to common dates
    // For computed metrics, we need lookback data
    const needsLookback = metricKey.startsWith('computed:');
    const { alignedSeries, alignedSeriesWithLookback } = alignSeriesData(
      historyData, 
      tickers, 
      range, 
      needsLookback
    );
    
    // Only compute returns for price-based metrics
    const shouldComputeReturns = metricKey === MetricKeys.PriceClose || metricKey.startsWith('computed:');
    
    let dailyReturnsMap: Record<string, number[]> = {};
    let btcReturns: number[] = [];
    let computedMetrics: Record<string, Record<string, SeriesData>> = {};
    
    if (shouldComputeReturns) {
      // Compute daily returns from full series with lookback (for rolling metrics)
      const seriesForReturns = needsLookback ? alignedSeriesWithLookback : alignedSeries;
      dailyReturnsMap = computeReturnsMap(seriesForReturns);
      
      // Compute BTC returns for comparative analysis (from display series)
      // Keep NaN values to maintain alignment with token returns
      const btcSeries = alignedSeries['BTC'];
      if (btcSeries) {
        btcReturns = computeDailyReturns(btcSeries.values);
      }
      
      // Compute rolling metrics (volatility, Sharpe, Sortino, drawdown)
      if (needsLookback) {
        computedMetrics = computeAllRollingMetrics(
          alignedSeriesWithLookback, 
          alignedSeries, 
          dailyReturnsMap, 
          tickers
        );
      }
    }

    const hasData = Object.keys(alignedSeries).length > 0 && 
                    Object.values(alignedSeries).some(s => s.dates.length > 1);

    return {
      alignedSeries,
      dailyReturnsMap,
      btcReturns,
      computedMetrics,
      hasData,
      isLoading,
      error: error ? error.message : null,
    };
  }, [historyData, tickers, range, metricKey, isLoading, error]);
}

